import React from 'react';
import { useMapStore } from '../../stores/mapStore';

export const AssessmentPanel: React.FC = () => {
  const { assessmentMode, setAssessmentMode, assessmentResult } = useMapStore();

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Site Assessment</h3>
        <p className="text-sm text-gray-600">
          Click anywhere on the map to assess tree planting suitability for that location.
        </p>
      </div>

      <div className="space-y-4">
        {/* Assessment Mode Toggle */}
        <div>
          <button
            onClick={() => setAssessmentMode(!assessmentMode)}
            className={`
              w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200
              ${assessmentMode 
                ? 'bg-forest-600 text-white hover:bg-forest-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            {assessmentMode ? 'Exit Assessment Mode' : 'Start Assessment'}
          </button>
        </div>

        {assessmentMode && (
          <div className="bg-forest-50 border border-forest-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-forest-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-forest-700 font-medium">
                Assessment mode active
              </span>
            </div>
            <p className="text-xs text-forest-600 mt-1">
              Click anywhere on the map to assess that location
            </p>
          </div>
        )}

        {/* Assessment Results */}
        {assessmentResult && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Assessment Results</h4>
            
            <div className="card p-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Overall Score</span>
                <span className={`
                  px-2 py-1 rounded text-sm font-medium
                  ${assessmentResult.overallScore >= 80 ? 'bg-green-100 text-green-800' :
                    assessmentResult.overallScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }
                `}>
                  {assessmentResult.overallScore}/100
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Heat Score</span>
                  <span className="font-medium">{assessmentResult.factors.heatScore}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Flood Risk</span>
                  <span className="font-medium">{assessmentResult.factors.floodRisk}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">EJ Score</span>
                  <span className="font-medium">{assessmentResult.factors.ejScore}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tree Count</span>
                  <span className="font-medium">{assessmentResult.factors.treeCount}</span>
                </div>
              </div>
            </div>

            {assessmentResult.recommendedSpecies.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Recommended Species</h5>
                <div className="space-y-2">
                  {assessmentResult.recommendedSpecies.slice(0, 3).map((rec, index) => (
                    <div key={index} className="bg-gray-50 rounded p-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Species #{rec.species_id}</span>
                        <span className="text-xs text-gray-500">{rec.suitability_score}% match</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            Assessment considers heat islands, flood zones, environmental justice factors, and existing tree density.
          </div>
        </div>
      </div>
    </div>
  );
};
